/**
 * Lawn Cost Calculator
 * Calculates annual lawn care costs based on size, grass type, region, and care approach
 */

import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { getRegionFromZipCode, toLegacyRegion } from "@/data/regional-climate"
import { REGIONAL_LAWN_COSTS, SERVICE_FREQUENCIES } from "@/data/lawn-care-costs"
import {
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  type DeterministicToolResultMetadata,
} from "@/lib/lawn-plan-engine"

export interface CostBreakdown {
  category: string
  description: string
  frequency: string
  costPer1000SqFt: number
  totalCost: number
}

export interface LawnCostResult extends DeterministicToolResultMetadata {
  lawnSize: number // sq ft
  grassType: string
  zipCode: string
  region: string
  serviceLevel: 'basic' | 'standard' | 'premium'
  careApproach: 'diy' | 'professional' | 'hybrid'
  
  // Cost breakdowns
  fertilizationCosts: CostBreakdown[]
  maintenanceCosts: CostBreakdown[]
  treatmentCosts: CostBreakdown[]
  equipmentCosts?: CostBreakdown[]
  waterCosts?: CostBreakdown
  
  // Summary
  monthlyTotal: number
  annualTotal: number
  costPer1000SqFt: number
  
  // Comparisons
  diyTotal?: number
  professionalTotal?: number
  savingsVsProfessional?: number
  
  // Recommendations
  costSavingTips: string[]
  budgetBreakdown: {
    category: string
    percentage: number
    amount: number
  }[]
}

export function calculateLawnCosts(
  lawnSize: number | string,
  grassType: string,
  zipCode: string,
  serviceLevel: 'basic' | 'standard' | 'premium',
  careApproach: 'diy' | 'professional' | 'hybrid',
  includeEquipment: boolean = false,
  includeWater: boolean = false,
  sunlight?: string | null,
): LawnCostResult {
  const context = buildCanonicalToolContext({
    toolId: 'cost',
    grassType,
    lawnSize,
    sunlight,
  })
  const canonicalLawnSize = context.lawnSizeSqft ?? Number.NaN
  const grass = GRASS_CHARACTERISTICS[context.grassType]
  const region = getRegionFromZipCode(zipCode)

  if (region === 'Unknown') {
    throw new Error('We could not determine your region from this ZIP code. Try a nearby ZIP.')
  }

  // Fine-grained climate sub-regions (California, Florida, etc.) keep their
  // identity in `region` for the result label, but the cost map is keyed on the
  // legacy region set. Prefer an exact entry when one exists (e.g. the cost map
  // ships a dedicated 'Pacific Northwest' table) and otherwise degrade to the
  // closest legacy parent so California/Florida/Desert Southwest still resolve.
  const costs = REGIONAL_LAWN_COSTS[region] ?? REGIONAL_LAWN_COSTS[toLegacyRegion(region)]

  if (!grass) {
    throw new Error('Cost data is not available for this grass type.')
  }

  if (!costs) {
    throw new Error('Regional cost data is not available for your area.')
  }
  
  const sqFtMultiplier = canonicalLawnSize / 1000
  
  // Determine fertilization frequency based on grass type and service level
  let fertilizationFrequency = SERVICE_FREQUENCIES.fertilization.moderate
  if (grass.fertilization.toLowerCase().includes('heavy')) {
    fertilizationFrequency = serviceLevel === 'premium' ? 
      SERVICE_FREQUENCIES.fertilization.heavy : 
      SERVICE_FREQUENCIES.fertilization.moderate
  } else if (grass.fertilization.toLowerCase().includes('light')) {
    fertilizationFrequency = serviceLevel === 'basic' ? 
      SERVICE_FREQUENCIES.fertilization.light : 
      SERVICE_FREQUENCIES.fertilization.moderate
  }
  
  // Calculate fertilization costs
  // Service tier maps to fertilizer grade so the cost ladder is meaningful:
  //   basic    → basic NPK (cheapest)
  //   standard → premium slow-release (mid)
  //   premium  → organic (most expensive)
  // Pre-fix, both basic and standard used 'basic' — the standard tier was a
  // dead branch and users paying for a higher service saw no cost difference
  // in the fertilization line.
  const fertilizationCosts: CostBreakdown[] = []
  const fertilizerType: 'basic' | 'premium' | 'organic' =
    serviceLevel === 'premium'
      ? 'organic'
      : serviceLevel === 'standard'
        ? 'premium'
        : 'basic'
  const fertilizerCostPer1000 = costs.fertilizer[fertilizerType]
  const fertilizerCost = fertilizerCostPer1000 * sqFtMultiplier * fertilizationFrequency.timesPerYear
  
  fertilizationCosts.push({
    category: 'Fertilization',
    description: `${fertilizerType.charAt(0).toUpperCase() + fertilizerType.slice(1)} fertilizer`,
    frequency: fertilizationFrequency.name,
    costPer1000SqFt: fertilizerCostPer1000 * fertilizationFrequency.timesPerYear,
    totalCost: fertilizerCost
  })
  
  // Calculate maintenance costs
  const maintenanceCosts: CostBreakdown[] = []
  const mowingFrequency = grass.season === 'Cool' ? 
    SERVICE_FREQUENCIES.mowing.coolSeason : 
    SERVICE_FREQUENCIES.mowing.warmSeason
  
  if (careApproach === 'professional' || careApproach === 'hybrid') {
    const mowingCost = costs.maintenance.mowingPerVisit * mowingFrequency.timesPerYear
    maintenanceCosts.push({
      category: 'Mowing',
      description: 'Professional mowing service',
      frequency: `${mowingFrequency.timesPerYear} times per year`,
      costPer1000SqFt: (mowingCost / sqFtMultiplier),
      totalCost: mowingCost
    })
  }
  
  // Calculate treatment costs based on service level
  const treatmentCosts: CostBreakdown[] = []
  
  if (serviceLevel === 'standard' || serviceLevel === 'premium') {
    // Weed control
    const weedControlCost = costs.treatments.weedControl * sqFtMultiplier * SERVICE_FREQUENCIES.treatments.weedControl.timesPerYear
    treatmentCosts.push({
      category: 'Weed Control',
      description: 'Pre and post-emergent herbicide',
      frequency: `${SERVICE_FREQUENCIES.treatments.weedControl.timesPerYear} applications per year`,
      costPer1000SqFt: costs.treatments.weedControl * SERVICE_FREQUENCIES.treatments.weedControl.timesPerYear,
      totalCost: weedControlCost
    })
    
    // Aeration
    const aerationFreq = SERVICE_FREQUENCIES.aeration[grass.season === 'Cool' ? 'coolSeason' : 'warmSeason']
    const aerationCost = costs.treatments.aeration * sqFtMultiplier * aerationFreq.timesPerYear
    treatmentCosts.push({
      category: 'Aeration',
      description: 'Core aeration',
      frequency: `${aerationFreq.timesPerYear} time per year`,
      costPer1000SqFt: costs.treatments.aeration * aerationFreq.timesPerYear,
      totalCost: aerationCost
    })
  }
  
  if (serviceLevel === 'premium') {
    // Pest control
    const pestControlCost = costs.treatments.pestControl * sqFtMultiplier * SERVICE_FREQUENCIES.treatments.pestControl.timesPerYear
    treatmentCosts.push({
      category: 'Pest Control',
      description: 'Insecticide treatment',
      frequency: `${SERVICE_FREQUENCIES.treatments.pestControl.timesPerYear} applications per year`,
      costPer1000SqFt: costs.treatments.pestControl * SERVICE_FREQUENCIES.treatments.pestControl.timesPerYear,
      totalCost: pestControlCost
    })
    
    // Overseeding (for cool season)
    if (grass.season === 'Cool') {
      const overseedingCost = costs.treatments.overseeding * sqFtMultiplier
      treatmentCosts.push({
        category: 'Overseeding',
        description: 'Fall overseeding',
        frequency: '1 time per year',
        costPer1000SqFt: costs.treatments.overseeding,
        totalCost: overseedingCost
      })
    }
  }
  
  // Equipment costs (one-time, if DIY)
  let equipmentCosts: CostBreakdown[] | undefined
  if (careApproach === 'diy' && includeEquipment) {
    equipmentCosts = []
    
    // Determine mower type based on lawn size
    let mowerCost = costs.equipment.pushMower
    let mowerType = 'Push mower'
    if (canonicalLawnSize > 8000) {
      mowerCost = costs.equipment.ridingMower
      mowerType = 'Riding mower'
    } else if (canonicalLawnSize > 4000) {
      mowerCost = costs.equipment.selfPropelledMower
      mowerType = 'Self-propelled mower'
    }
    
    equipmentCosts.push({
      category: 'Mower',
      description: mowerType,
      frequency: 'One-time (amortized over 5 years)',
      costPer1000SqFt: (mowerCost / 5) / sqFtMultiplier,
      totalCost: mowerCost / 5 // Amortize over 5 years
    })
    
    equipmentCosts.push({
      category: 'Spreader',
      description: 'Broadcast spreader',
      frequency: 'One-time (amortized over 10 years)',
      costPer1000SqFt: (costs.equipment.spreader / 10) / sqFtMultiplier,
      totalCost: costs.equipment.spreader / 10
    })
  }
  
  // Water costs
  let waterCosts: CostBreakdown | undefined
  if (includeWater) {
    const waterCostPerMonth = costs.maintenance.waterPerMonth
    const waterCostPerYear = waterCostPerMonth * 12
    waterCosts = {
      category: 'Water',
      description: 'Irrigation water costs',
      frequency: '12 months per year',
      costPer1000SqFt: waterCostPerYear / sqFtMultiplier,
      totalCost: waterCostPerYear
    }
  }
  
  // Calculate totals
  const fertilizationTotal = fertilizationCosts.reduce((sum, item) => sum + item.totalCost, 0)
  const maintenanceTotal = maintenanceCosts.reduce((sum, item) => sum + item.totalCost, 0)
  const treatmentTotal = treatmentCosts.reduce((sum, item) => sum + item.totalCost, 0)
  const equipmentTotal = equipmentCosts?.reduce((sum, item) => sum + item.totalCost, 0) || 0
  const waterTotal = waterCosts?.totalCost || 0
  
  const annualTotal = fertilizationTotal + maintenanceTotal + treatmentTotal + equipmentTotal + waterTotal
  const monthlyTotal = annualTotal / 12
  const costPer1000SqFt = annualTotal / sqFtMultiplier
  
  // Calculate comparison costs
  let diyTotal: number | undefined
  let professionalTotal: number | undefined
  let savingsVsProfessional: number | undefined
  
  // Always compute diy + professional comparison totals (regardless of the
  // user's selected careApproach) so the upper "vs other approaches" mini-bar
  // and the lower comparison card read from the same numbers.
  diyTotal = fertilizationTotal + treatmentTotal + (includeEquipment ? equipmentTotal : 0) + waterTotal

  // Synthetic pro estimate. Pre-fix this used (materials × 1.3), but for DIY
  // users `maintenanceTotal` is 0 (no pro mowing visits), so the comparison
  // massively under-stated pro cost. Real pro service is mostly labor:
  // ~30 mowing visits/year × ~$45 each is typically the largest line.
  // Recalculate pro mowing inline so the comparison reflects what a homeowner
  // would actually pay.
  {
    const proMowingCost = costs.maintenance.mowingPerVisit * mowingFrequency.timesPerYear
    professionalTotal =
      proMowingCost +
      (fertilizationTotal + treatmentTotal + waterTotal) * 1.3
  }
  
  if (professionalTotal && careApproach === 'diy') {
    savingsVsProfessional = professionalTotal - annualTotal
  } else if (professionalTotal && careApproach === 'hybrid') {
    savingsVsProfessional = professionalTotal - annualTotal
  }
  
  // Budget breakdown. Guard against the rare zero-total edge case (basic+DIY in
  // a region where all rate fields are 0) — without this, every percentage
  // becomes NaN/Infinity and breaks the renderer downstream.
  const pct = (n: number) => (annualTotal > 0 ? (n / annualTotal) * 100 : 0)
  const budgetBreakdown = [
    { category: 'Fertilization', percentage: pct(fertilizationTotal), amount: fertilizationTotal },
    { category: 'Maintenance', percentage: pct(maintenanceTotal), amount: maintenanceTotal },
    { category: 'Treatments', percentage: pct(treatmentTotal), amount: treatmentTotal }
  ]

  if (equipmentTotal > 0) {
    budgetBreakdown.push({ category: 'Equipment', percentage: pct(equipmentTotal), amount: equipmentTotal })
  }

  if (waterTotal > 0) {
    budgetBreakdown.push({ category: 'Water', percentage: pct(waterTotal), amount: waterTotal })
  }
  
  // Cost saving tips
  const costSavingTips: string[] = [
    'Buy fertilizer and seed in bulk during off-season sales',
    'Consider grass cycling (leaving clippings) to reduce fertilizer needs by 25%',
    'Adjust mower height to optimal range - taller grass requires less water',
    'Water deeply but infrequently to encourage deeper roots and reduce water costs',
    'Use a soil test ($15-30) before fertilizing to avoid wasting money on unnecessary nutrients',
    'Join a local lawn care co-op to share equipment and reduce individual costs'
  ]
  
  if (careApproach === 'professional') {
    costSavingTips.push(
      'Consider DIY for simple tasks like mowing and watering to save 40-60% annually',
      'Bundle services with neighbors for group discounts (typically 10-15% off)'
    )
  }
  
  if (serviceLevel === 'premium') {
    costSavingTips.push(
      'Downgrade to standard service during economic downturns - your lawn will still look good',
      'Skip optional treatments during mild weather years'
    )
  }
  
  return attachToolEngineMetadata({
    lawnSize: canonicalLawnSize,
    grassType: grass.name,
    zipCode,
    region,
    serviceLevel,
    careApproach,
    fertilizationCosts,
    maintenanceCosts,
    treatmentCosts,
    equipmentCosts,
    waterCosts,
    monthlyTotal: parseFloat(monthlyTotal.toFixed(2)),
    annualTotal: parseFloat(annualTotal.toFixed(2)),
    costPer1000SqFt: parseFloat(costPer1000SqFt.toFixed(2)),
    diyTotal: diyTotal ? parseFloat(diyTotal.toFixed(2)) : undefined,
    professionalTotal: professionalTotal ? parseFloat(professionalTotal.toFixed(2)) : undefined,
    savingsVsProfessional: savingsVsProfessional ? parseFloat(savingsVsProfessional.toFixed(2)) : undefined,
    costSavingTips,
    budgetBreakdown: budgetBreakdown.map(item => ({
      ...item,
      percentage: parseFloat(item.percentage.toFixed(1)),
      amount: parseFloat(item.amount.toFixed(2))
    }))
  }, context)
}
